import { FC, useState, useEffect } from 'react';
import { InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ApplyDiscountHeader,
  DefaultLabel,
  StyledSwitch,
  DiscountContainer,
} from './Discounts.styles';
import {
  FormContainer,
  ScrollableContent,
  Container,
  LeftColumn,
  HeaderRow,
  LabelTypography,
  Spacer24,
  ItemCard,
  ItemRow,
  SelectBox,
  StyledAutocomplete,
  StyledAutocompleteTextField,
  ErrorTextSelect,
  AmountBox,
  StyledTextField,
  ErrorTextAmount,
  RightPanel,
  FinalTotalsTitle,
  TotalsRow,
  TotalLabel,
  TotalValue,
  SurchargeRow,
  GrandTotalBox,
  NextButtonContainer,
  BackButton,
  NextButton,
} from './Surcharges.styles';
import { OrderItem, Discount } from 'src/models/Management.interface';
import { getBasicDiscounts } from 'src/services/discount.service';
import { DiscountLabelRed } from './Summary.styles';

interface DiscountsProps {
  onNext: () => void;
  onBack: () => void;
}

const Discounts: FC<DiscountsProps> = ({ onNext, onBack }) => {
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [itemsSubtotal, setItemsSubtotal] = useState(0);
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    const init = async () => {
      await fetchDiscounts();
      calculateItemsSubtotal();
      loadSavedData();
    };
    init();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const data = await getBasicDiscounts();
      setDiscounts(data);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const calculateItemsSubtotal = () => {
    const step2Data = localStorage.getItem('orderStep2');
    if (step2Data) {
      const { items } = JSON.parse(step2Data);
      const total = items.reduce(
        (sum: number, item: OrderItem) => sum + item.quantity * item.unitPrice,
        0
      );
      setItemsSubtotal(total);
    }
  };

  const loadSavedData = () => {
    const savedData = localStorage.getItem('orderStep3');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      const savedApply = !!parsedData.applyDiscount;
      setApplyDiscount(savedApply);
      formik.setFieldValue('applyDiscount', savedApply);

      const savedDiscountId =
        parsedData.discountId !== undefined && parsedData.discountId !== null
          ? String(parsedData.discountId)
          : '';

      if (savedDiscountId) {
        formik.setFieldValue('discountId', savedDiscountId);

        if (
          parsedData.discountValue === undefined ||
          parsedData.discountValue === null ||
          parsedData.discountValue === ''
        ) {
          const sd = discounts.find((d) => String(d.id) === savedDiscountId);
          if (sd) {
            formik.setFieldValue(
              'discountValue',
              sd.percentage ? sd.percentage : sd.defaultValue ?? ''
            );
          }
        } else {
          formik.setFieldValue('discountValue', parsedData.discountValue);
        }
      } else {
        if (
          parsedData.discountValue !== undefined &&
          parsedData.discountValue !== null &&
          parsedData.discountValue !== ''
        ) {
          formik.setFieldValue('discountValue', parsedData.discountValue);
        }
      }
    }
  };
  const validationSchema = Yup.object().shape({
    discountId: Yup.string().when('applyDiscount', {
      is: true,
      then: (schema) => schema.required('Discount is required'),
      otherwise: (schema) => schema,
    }),
    discountValue: Yup.number().when('applyDiscount', {
      is: true,
      then: (schema) =>
        schema
          .min(0, 'Value must be positive')
          .test(
            'max-percentage',
            'Percentage cannot exceed 100',
            function (value) {
              const { discountId } = this.parent;
              const selectedDiscount = discounts.find(
                (d) => d.id === discountId
              );
              if (selectedDiscount?.percentage && value != null) {
                return value <= 100;
              }
              return true;
            }
          )
          .test(
            'max-amount',
            'Discount cannot exceed subtotal',
            function (value) {
              const { discountId } = this.parent;
              const selectedDiscount = discounts.find(
                (d) => d.id === discountId
              );
              if (
                selectedDiscount &&
                !selectedDiscount.percentage &&
                value != null
              ) {
                return value <= itemsSubtotal;
              }
              return true;
            }
          )
          .required('Value is required'),
      otherwise: (schema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues: {
      discountId: '',
      discountValue: '',
      applyDiscount: applyDiscount,
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      const dataToSave = {
        applyDiscount,
        discountId: applyDiscount ? values.discountId : null,
        discountValue: applyDiscount ? values.discountValue : null,
        discountAmount: applyDiscount ? discountAmount : 0,
        discountName:
          applyDiscount && selectedDiscount ? selectedDiscount.name : '',
        discountPercentage:
          applyDiscount && selectedDiscount && selectedDiscount.percentage
            ? values.discountValue !== '' && values.discountValue !== undefined
              ? Number(values.discountValue)
              : Number(selectedDiscount.percentage)
            : null,
        discounts: discounts,
      };
      localStorage.setItem('orderStep3', JSON.stringify(dataToSave));
      onNext();
    },
  });

  const selectedDiscount = discounts.find(
    (d) => d.id === formik.values.discountId
  );

  useEffect(() => {
    formik.validateForm();
  }, [applyDiscount]);

  const calculateDiscountAmount = () => {
    if (!applyDiscount || !selectedDiscount) return 0;

    const formValue =
      formik.values.discountValue !== '' &&
      formik.values.discountValue !== undefined
        ? Number(formik.values.discountValue)
        : NaN;

    const rawValue = !isNaN(formValue)
      ? formValue
      : selectedDiscount.percentage
      ? Number(selectedDiscount.percentage)
      : selectedDiscount.defaultValue
      ? Number(selectedDiscount.defaultValue)
      : 0;

    if (selectedDiscount.percentage) {
      return (itemsSubtotal * rawValue) / 100;
    }
    return rawValue;
  };

  const discountAmount = calculateDiscountAmount();
  const netTotal = itemsSubtotal - discountAmount;

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setApplyDiscount(checked);
    formik.setFieldValue('applyDiscount', checked);

    if (!checked) {
      formik.setFieldValue('discountId', '');
      formik.setFieldValue('discountValue', '');
      formik.setFieldError('discountId', '');
      formik.setFieldError('discountValue', '');
    } else {
      formik.validateField('discountId');
    }
  };

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <ScrollableContent>
        <Container>
          <LeftColumn>
            <ApplyDiscountHeader>
              <DefaultLabel>Apply Discount</DefaultLabel>
              <StyledSwitch
                checked={applyDiscount}
                onChange={handleToggleChange}
              />
            </ApplyDiscountHeader>
            {applyDiscount && (
              <DiscountContainer>
                <HeaderRow>
                  <LabelTypography>Discount</LabelTypography>
                  <LabelTypography>Amount/Percentage</LabelTypography>
                  <Spacer24 />
                </HeaderRow>
                <ItemCard>
                  <ItemRow>
                    <SelectBox>
                      <StyledAutocomplete
                        options={discounts}
                        getOptionLabel={(option) => option.name}
                        value={
                          discounts.find(
                            (d) => d.id === formik.values.discountId
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          const id = newValue?.id || '';
                          formik.setFieldValue('discountId', id);
                          if (newValue) {
                            formik.setFieldValue(
                              'discountValue',
                              newValue.percentage
                                ? newValue.percentage
                                : newValue.defaultValue || ''
                            );
                          } else {
                            formik.setFieldValue('discountValue', '');
                          }
                        }}
                        onBlur={formik.handleBlur}
                        ListboxProps={{
                          style: {
                            maxHeight: '350px',
                            overflow: 'auto',
                          },
                        }}
                        renderInput={(params) => (
                          <StyledAutocompleteTextField
                            {...params}
                            name="discountId"
                            placeholder="Select discount"
                            hasValue={!!formik.values.discountId}
                            hasError={
                              formik.touched.discountId &&
                              !!formik.errors.discountId
                            }
                          />
                        )}
                      />
                      {(formik.touched.discountId || formik.submitCount > 0) &&
                        formik.errors.discountId && (
                          <ErrorTextSelect>
                            {formik.errors.discountId}
                          </ErrorTextSelect>
                        )}
                    </SelectBox>
                    <AmountBox>
                      <StyledTextField
                        name="discountValue"
                        type="text"
                        value={formik.values.discountValue || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const val = Number(value);
                            if (selectedDiscount?.percentage && val > 100) {
                              formik.setFieldError(
                                'discountValue',
                                'Percentage cannot exceed 100'
                              );
                              return;
                            }
                            if (
                              selectedDiscount &&
                              !selectedDiscount.percentage &&
                              val > itemsSubtotal
                            ) {
                              formik.setFieldError(
                                'discountValue',
                                'Discount amount cannot exceed items subtotal'
                              );
                              formik.setFieldValue(
                                'discountValue',
                                value === '' ? 0 : value
                              );
                              return;
                            }
                            formik.setFieldError('discountValue', undefined);
                            formik.setFieldValue(
                              'discountValue',
                              value === '' ? 0 : value
                            );
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          (formik.touched.discountValue ||
                            formik.submitCount > 0) &&
                          Boolean(formik.errors.discountValue)
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedDiscount &&
                              selectedDiscount.percentage ? (
                                '%'
                              ) : (
                                <i className="bx bx-money" aria-hidden="true" />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      {(formik.touched.discountValue ||
                        formik.submitCount > 0) &&
                        formik.errors.discountValue && (
                          <ErrorTextAmount>
                            {formik.errors.discountValue}
                          </ErrorTextAmount>
                        )}
                    </AmountBox>
                  </ItemRow>
                </ItemCard>
              </DiscountContainer>
            )}
          </LeftColumn>
          <RightPanel>
            <FinalTotalsTitle>Totals Summary</FinalTotalsTitle>
            <TotalsRow>
              <TotalLabel>Items subtotal</TotalLabel>
              <TotalValue>
                {itemsSubtotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </TotalsRow>
            <SurchargeRow>
              <DiscountLabelRed>Discount</DiscountLabelRed>
              <DiscountLabelRed>
                {discountAmount > 0 ? '-' : ''}{' '}
                {discountAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </DiscountLabelRed>
            </SurchargeRow>
            <GrandTotalBox>
              <TotalLabel>Net Total</TotalLabel>
              <TotalValue>
                {netTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </GrandTotalBox>
          </RightPanel>
        </Container>
      </ScrollableContent>
      <NextButtonContainer>
        <BackButton onClick={onBack}>Back</BackButton>
        <NextButton type="submit">Next</NextButton>
      </NextButtonContainer>
    </FormContainer>
  );
};

export default Discounts;
